import React, { useState } from "react";
import {
  IconCalendarEvent,
  IconDiscount,
  IconRocket,
  IconFileText,
  IconCalendarCheck,
  IconPencil,
  IconSparkles,
  IconCheck,
  IconArrowRight,
} from "@tabler/icons-react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { useCampaignStore } from "../../../../../stores/CampaignStore";

const TOPICS = [
  {
    id: "event",
    label: "Promouvoir un événement",
    hint: "Webinaire, salon, conférence…",
    icon: IconCalendarEvent,
    fields: [
      {
        key: "name",
        label: "Nom de l'événement",
        placeholder: "Ex. Webinaire Future of Work",
        required: true,
      },
      {
        key: "date",
        label: "Date (ou période)",
        placeholder: "Ex. 12 septembre 2026",
        required: true,
      },
      {
        key: "place",
        label: "Lieu",
        placeholder: "Ex. En ligne / Paris",
        optional: true,
      },
      {
        key: "link",
        label: "Lien de l'événement",
        placeholder: "https://…",
        optional: true,
      },
    ],
  },
  {
    id: "offer",
    label: "Mettre en avant une offre",
    hint: "Promotion, remise, essai gratuit…",
    icon: IconDiscount,
    fields: [
      {
        key: "name",
        label: "Nom de l'offre",
        placeholder: "Ex. -30 % sur le plan Growth",
        required: true,
      },
      {
        key: "message",
        label: "Message clé",
        placeholder: "Ce que le prospect doit retenir",
        textarea: true,
      },
      {
        key: "link",
        label: "Lien de l'offre",
        placeholder: "https://…",
        optional: true,
      },
    ],
  },
  {
    id: "product",
    label: "Lancer un nouveau produit ou service",
    hint: "Nouveauté, fonctionnalité, gamme…",
    icon: IconRocket,
    fields: [
      {
        key: "name",
        label: "Nom du produit",
        placeholder: "Ex. Magileads Copilot",
        required: true,
      },
      {
        key: "message",
        label: "Message clé",
        placeholder: "Le bénéfice principal",
        textarea: true,
      },
      { key: "link", label: "Lien", placeholder: "https://…", optional: true },
    ],
  },
  {
    id: "content",
    label: "Partager un contenu",
    hint: "Livre blanc, étude, article…",
    icon: IconFileText,
    fields: [
      {
        key: "title",
        label: "Titre du contenu",
        placeholder: "Ex. Étude 2026 sur la prospection B2B",
        required: true,
      },
      { key: "link", label: "Lien", placeholder: "https://…", required: true },
    ],
  },
  {
    id: "meetings",
    label: "Obtenir des rendez-vous sur mon agenda",
    hint: "Prise de rendez-vous directe",
    icon: IconCalendarCheck,
    fields: [
      {
        key: "link",
        label: "Lien Calendly / agenda / formulaire, etc...",
        placeholder: "https://calendly.com/…",
        required: true,
      },
    ],
  },
  {
    id: "other",
    label: "Autre",
    hint: "Décrivez votre sujet",
    icon: IconPencil,
    fields: [
      {
        key: "freeText",
        label: "Décrivez le sujet de la campagne",
        placeholder: "En une ou deux phrases…",
        textarea: true,
        required: true,
      },
    ],
  },
];

// Fonction utilitaire pour générer la string complète
function generateContextValue(topicId, currentFields) {
  if (!topicId) return "";

  const topic = TOPICS.find((t) => t.id === topicId);
  if (!topic) return "";

  // On commence la string avec le nom du sujet principal
  let parts = [`Sujet de la campagne : ${topic.label}`];

  // On parcourt les champs du sujet pour ajouter les infos renseignées
  topic.fields.forEach((field) => {
    const val = currentFields[field.key];
    if (val && val.trim() !== "") {
      parts.push(`${field.label} : ${val}`);
    }
  });

  // On joint tout avec des points
  return parts.join(". ") + ".";
}

function StepContext({ setActive }) {
  const programData = useCampaignStore((s) => s.programData);
  const setProgramData = useCampaignStore((s) => s.setProgramData);

  const agentName = programData?.agent?.name || "L'agent";
  const agentAvatar = programData?.agent?.avatar || null;
  const agentColor = programData?.agent?.color || "gray";

  const initial = agentName.trim().charAt(0).toUpperCase() || "A";

  const [expanded, setExpanded] = useState(
    programData?.context?.topicId || null,
  );

  const onChange = (newContext) => {
    setProgramData({
      ...programData,
      context: newContext,
    });
  };

  const handleNext = () => {
    setActive("audience");
  };

  function selectTopic(id) {
    if (programData?.context?.topicId === id) {
      // Désélection : on vide tout, y compris la 'value'
      onChange({ topicId: null, fields: {}, value: "" });
      setExpanded(null);
      return;
    }

    // Nouvelle sélection : on initialise avec une value de base
    const initialValue = generateContextValue(id, {});
    onChange({ topicId: id, fields: {}, value: initialValue });
    setExpanded(id);
  }

  function setField(key, value) {
    const newFields = {
      ...(programData?.context?.fields || {}),
      [key]: value,
    };

    // On génère la nouvelle string en combinant le sujet et les nouveaux champs
    const newValueString = generateContextValue(
      programData?.context?.topicId,
      newFields,
    );

    onChange({
      ...programData?.context,
      fields: newFields,
      value: newValueString, // On injecte le texte généré !
    });
  }

  const noneSelected = expanded === null;
  function selectNone() {
    onChange({ topicId: null, fields: {}, value: "" });
    setExpanded(null);
  }

  const activeTopic = TOPICS.find((t) => t.id === expanded) ?? null;

  const handleSubmit = (e) => {
    e.preventDefault();
    handleNext();
  };

  return (
    <Box mx="auto" p="xl" component="form" onSubmit={handleSubmit}>
      <Stack gap={4} mb="xl">
        <Text c="violet.5" fw={600} size="md">
          Étape 2 · Contexte (facultatif)
        </Text>
        <Text component="h1" size="xl" fw={700} m={0} style={{ fontSize: 24 }}>
          Souhaitez-vous mettre en avant un sujet spécifique ?
        </Text>
        <Text c="dimmed">
          Cette étape est facultative. Elle permet à l'agent d'adapter
          automatiquement ses messages à cette campagne.
        </Text>
      </Stack>

      {/* OPTION PAR DÉFAUT : AUCUN SUJET PRÉCIS */}
      <UnstyledButton
        onClick={selectNone}
        w="100%"
        mb="xl"
        style={{ display: "block" }}
      >
        <Paper
          withBorder
          p="lg"
          radius="xl"
          style={{
            borderColor: noneSelected
              ? "var(--mantine-color-violet-5)"
              : "var(--mantine-color-gray-3)",
            backgroundColor: noneSelected
              ? "var(--mantine-color-violet-0)"
              : "white",
            boxShadow: noneSelected
              ? "0 4px 12px rgba(132, 94, 247, 0.1)"
              : "0 2px 4px rgba(0,0,0,0.02)",
            transition: "all 0.2s ease",
          }}
        >
          <Group wrap="nowrap" justify="space-between">
            <Group wrap="nowrap" gap="lg" align="center">
              {/* Avatar de l'agent */}
              <Box pos="relative">
                {agentAvatar ? (
                  <Avatar
                    src={agentAvatar}
                    size={64}
                    radius="xl"
                    style={{
                      border: "2px solid white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                ) : (
                  <Avatar size={64} radius="xl">
                    {initial}
                  </Avatar>
                )}
                <ThemeIcon
                  size={22}
                  radius="xl"
                  color="violet"
                  pos="absolute"
                  bottom={-4}
                  right={-4}
                  style={{ border: "2px solid white" }}
                >
                  <IconSparkles size={12} />
                </ThemeIcon>
              </Box>

              <Box style={{ flex: 1 }}>
                <Group gap="xs" mb={4}>
                  <Text fw={700} size="lg">
                    Laisser {agentName} piloter la campagne
                  </Text>
                  <Badge variant="light" color="violet" size="sm" radius="sm">
                    Le plus utilisé
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed" lh={1.5} maw={600}>
                  {agentName} utilise les connaissances acquises lors de sa
                  configuration pour adapter automatiquement ses messages à
                  cette campagne.
                </Text>
              </Box>
            </Group>

            {/* Checkbox status */}
            <Box>
              <ThemeIcon
                size={28}
                radius="xl"
                variant={noneSelected ? "filled" : "outline"}
                color={noneSelected ? "violet" : "gray.3"}
              >
                <IconCheck
                  size={16}
                  color={noneSelected ? "white" : "transparent"}
                  stroke={3}
                />
              </ThemeIcon>
            </Box>
          </Group>
        </Paper>
      </UnstyledButton>

      {/* SÉPARATEUR */}
      <Divider
        my="xl"
        label={
          <Text size="sm" c="dimmed" fw={500}>
            Ou mettez en avant un sujet précis
          </Text>
        }
        labelPosition="center"
      />
      {/* GRILLE DES SUJETS */}
      <Grid gutter="md">
        {TOPICS.map((topic) => {
          const selected = programData?.context?.topicId === topic.id;
          const Icon = topic.icon;

          return (
            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={topic.id}>
              <UnstyledButton
                onClick={() => selectTopic(topic.id)}
                w="100%"
                style={{ display: "block" }}
              >
                <Paper
                  withBorder
                  p="xl"
                  radius="xl"
                  pos="relative"
                  style={{
                    borderColor: selected
                      ? "var(--mantine-color-violet-4)"
                      : "var(--mantine-color-gray-3)",
                    transition: "all 0.2s ease",
                    height: "100%",
                  }}
                  bg={selected ? "var(--mantine-color-violet-0)" : "white"}
                >
                  <Group wrap="nowrap" align="center" gap={"xl"}>
                    <ThemeIcon
                      size={64}
                      radius="lg"
                      variant={selected ? "filled" : "light"}
                      color="violet"
                    >
                      <Icon size={32} />
                    </ThemeIcon>
                    <Box style={{ flex: 1 }}>
                      <Text size="lg" fw={600} lh={1.2}>
                        {topic.label}
                      </Text>
                      <Text size="md" c="dimmed" mt={4}>
                        {topic.hint}
                      </Text>
                    </Box>
                  </Group>

                  {/* Pastille check si sélectionné */}
                  {selected && (
                    <ThemeIcon
                      size={20}
                      radius="xl"
                      color="violet"
                      pos="absolute"
                      top={12}
                      right={12}
                    >
                      <IconCheck size={12} stroke={3} />
                    </ThemeIcon>
                  )}
                </Paper>
              </UnstyledButton>
            </Grid.Col>
          );
        })}
      </Grid>

      {/* FORMULAIRE DU SUJET SÉLECTIONNÉ */}
      {activeTopic && (
        <Paper
          mt="xl"
          p="xl"
          radius="xl"
          withBorder
          bg="violet.0"
          style={{ borderColor: "var(--mantine-color-violet-2)" }}
        >
          <Group gap="xs" mb="lg">
            <IconSparkles size={16} color="var(--mantine-color-violet-6)" />
            <Text c="violet.6" fw={600} size="lg">
              Détails du sujet — {activeTopic.label}
            </Text>
          </Group>

          <Grid gutter="md">
            {activeTopic.fields.map((field) => (
              <Grid.Col
                span={{ base: 12, sm: field.textarea ? 12 : 6 }}
                key={field.key}
              >
                {field.textarea ? (
                  <Textarea
                    label={field.label}
                    description={field.optional ? "(optionnel)" : null}
                    placeholder={field.placeholder}
                    value={programData?.context?.fields?.[field.key] ?? ""}
                    onChange={(e) => setField(field.key, e.currentTarget.value)}
                    minRows={3}
                    required={field.required}
                    autosize
                    radius="md"
                    size="md"
                  />
                ) : (
                  <TextInput
                    label={field.label}
                    description={field.optional ? "(optionnel)" : null}
                    placeholder={field.placeholder}
                    value={programData?.context?.fields?.[field.key] ?? ""}
                    onChange={(e) => setField(field.key, e.currentTarget.value)}
                    required={field.required}
                    radius="md"
                    size="md"
                  />
                )}
              </Grid.Col>
            ))}
          </Grid>
        </Paper>
      )}

      {/* MESSAGE SI AUCUN SUJET */}
      {!activeTopic && (
        <Paper
          mt="xl"
          p="lg"
          radius="xl"
          withBorder
          bg="gray.0"
          style={{
            borderStyle: "dashed",
            borderColor: "var(--mantine-color-gray-4)",
          }}
        >
          <Text ta="center" c="dimmed" size="lg">
            Aucun sujet sélectionné : l'agent utilisera simplement les
            informations définies lors de sa configuration. Vous pouvez passer
            cette étape.
          </Text>
        </Paper>
      )}

      <Group justify="flex-end" mt="xl">
        <Button
          color="primary"
          radius="xl"
          size="md"
          rightSection={<IconArrowRight size={18} />}
          //   disabled={!agentId}
          type="submit"
        >
          Continuer
        </Button>
      </Group>
    </Box>
  );
}

export default StepContext;
